import { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { type PersonAvatarVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible, Pencil } from '@factorialco/f0-react/icons/app'
import {
  PerformanceProcessTemplateHeaderEstimatedTargetFragment,
  PerformanceProcessTemplateHeaderFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { reviewTypePicker } from 'modules/performance_process/components/common/filters/reviewTypePicker'
import { sectionSearchParam } from 'modules/performance_process/components/review/EditionWindow/editionWindowOptions'
import { CustomTemplateQuestionnairePreviewSidepanel } from 'modules/performance_process/components/templates/CustomTemplateQuestionnairePreviewSidepanel'

interface TemplateHeaderProps {
  template: PerformanceProcessTemplateHeaderFragment
  firstEstimatedTargets: PerformanceProcessTemplateHeaderEstimatedTargetFragment[]
  estimatedTargetsCount: number
}

export const TemplateHeader: FC<TemplateHeaderProps> = ({
  template,
  firstEstimatedTargets,
  estimatedTargetsCount,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { templateId } = useParams()

  const [previewSidepanelOpen, setPreviewSidepanelOpen] = useState(false)

  const settingsPath = navigation.performanceProcess.reviews.templates.template.settings.fullPath({
    templateId: Number(templateId),
  })

  const isMissingReviewerStrategy = !template.reviewerStrategies?.length

  return (
    <>
      <ResourceHeader
        title={template.name}
        description={template.description || i18n.t('performance_process.reviews.no_description')}
        secondaryActions={[
          !isMissingReviewerStrategy && {
            label: i18n.t('performance_process.reviews.preview'),
            icon: EyeVisible,
            onClick: () => setPreviewSidepanelOpen(true),
          },
          {
            label: i18n.t('performance_process.reviews.go_to_edit_review_button'),
            icon: Pencil,
            onClick: () => navigate(settingsPath),
          },
        ].filter(Boolean)}
        metadata={[
          {
            label: i18n.t('performance_process.reviews.launch_review_types'),
            value: {
              type: 'text',
              content:
                template.reviewerStrategies?.map((type) => reviewTypePicker(type)).join(' • ') ||
                i18n.t('performance_process.reviews.missing_review_type_header'),
            },
            actions: [
              {
                label: i18n.t('performance_process.reviews.missing_review_type_header'),
                icon: Pencil,
                onClick: () => navigate(settingsPath),
              },
            ],
          },
          {
            label: i18n.t('performance_process.reviews.audience.title'),
            value: !estimatedTargetsCount
              ? {
                  type: 'text',
                  content: i18n.t('performance_process.reviews.missing_target_header'),
                }
              : estimatedTargetsCount > 10
              ? {
                  type: 'text',
                  content: i18n.t('performance_process.reviews.audience.participants_count', {
                    count: estimatedTargetsCount,
                  }),
                }
              : {
                  type: 'list',
                  variant: 'person',
                  avatars: firstEstimatedTargets.map(
                    (target): PersonAvatarVariant => ({
                      firstName: target.access?.firstName ?? '',
                      lastName: target.access?.lastName ?? '',
                      type: 'person',
                      src: buildUrl(target.access?.avatar),
                    })
                  ),
                },
            actions: [
              {
                label: i18n.t('common.edit'),
                icon: Pencil,
                onClick: () => navigate(settingsPath + sectionSearchParam('target_group')),
              },
            ],
          },
        ]}
      />
      <CustomTemplateQuestionnairePreviewSidepanel
        isOpen={previewSidepanelOpen}
        onClose={() => setPreviewSidepanelOpen(false)}
      />
    </>
  )
}
